/**
 * Baccarat bot. There is no skill in the game itself, so bots behave like casual players: a modest bet (a small multiple
 * of the minimum, never more than a tenth of the stack), mostly on Banker or Player, now and then following the last
 * winner (a "streak" player), rarely on Tie, and occasionally sitting a hand out.
 * The bot only ever reads its own seat's view.
 */
import { chance, type BotEngine } from '../../bots/types.js';
import type { BaccaratAction, BaccaratLegal, BaccaratSide, BaccaratView } from './engine.js';

export const baccaratBot: BotEngine<BaccaratView, BaccaratLegal, BaccaratAction> = {
  decideAction(view, legal, ctx) {
    return this.chooseBet!(view, legal, ctx);
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bet') return this.handleTimeout(view, legal, ctx);
    if (legal.canSitOut && chance(ctx, 0.03)) return { type: 'sitOut' };
    const me = view.seat === null ? null : view.players[view.seat];
    const multiple = 1 + ctx.rng.int(ctx.difficulty === 'BEGINNER' ? 4 : 3);
    const cap = me ? Math.max(legal.min, Math.floor(me.chips / 10)) : legal.min;
    const amount = Math.max(legal.min, Math.min(legal.min * multiple, cap, legal.max));
    const last: BaccaratSide | null = view.lastResult?.winner ?? null;
    let side: BaccaratSide;
    if (last !== null && last !== 'TIE' && chance(ctx, 0.3)) side = last;
    else {
      const roll = ctx.rng.next();
      side = roll < 0.08 ? 'TIE' : roll < 0.54 ? 'BANKER' : 'PLAYER';
    }
    return { type: 'bet', side, amount };
  },

  evaluateState(view) {
    const me = view.seat === null ? null : view.players[view.seat];
    return me ? Math.max(0, Math.min(1, me.chips / (view.config.minBet * 200))) : 0;
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout() {
    return { type: 'sitOut' };
  },
};
